// React
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, Stack, Typography } from "@mui/material";
import { PersonAddAlt1Outlined } from "@mui/icons-material";
import { DataGrid, GridOverlay, GridToolbar } from "@mui/x-data-grid";
import { PatientDrawer } from "Components/PatientDrawer";
import PatientTableFooter from "./PatientTableFooter";

// Constants
import { BUTTON_BORDER_RADIUS } from "Constants/theme";

// Utils
import {
  getPatientTableColumns,
  getPatientTableRowId,
  sortPatientTableRows,
} from "./patientTableColumns";
import { isAllZeroPhin } from "Utils/Normalization";

const PatientTable = ({
  patients,
  setPatients,
  matchesCurrentSearch,
  loading = true,
  handleSearch,
  handleSearchMore,
  canSearchMore,
  scannedCardData = null,
  searchValues = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Table
  const columns = useMemo(() => getPatientTableColumns(t), [t]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  // Patients
  const sortedPatients = useMemo(
    () => sortPatientTableRows(patients),
    [patients]
  );
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handleRowClick = (params) => {
    setSelectedPatient(params.row);
    setOpenDrawer(true);
  };

  const bookAppointment = (patient) => {
    navigate("/mbs/reason", { state: { patient } });
  };

  const disableCreatePatient =
    Boolean(searchValues?.healthCardNumber) &&
    patients.some((p) => p.inSystem === false) &&
    !isAllZeroPhin(searchValues?.healthCardNumber);

  const EmptyStateOverlay = () => (
    <GridOverlay>
      <Typography variant="body1" color="text.secondary">
        {t("mbsSearch.table.noMatches")}
      </Typography>
    </GridOverlay>
  );

  const hasPrefill = Boolean(
    searchValues?.lastName ||
    searchValues?.dateOfBirth ||
    searchValues?.healthCardNumber
  );

  const handleCreateGuest = () => {
    const navigationState = {};

    if (scannedCardData) {
      navigationState.scannedCard = scannedCardData;
    }

    if (hasPrefill) {
      navigationState.searchValues = searchValues;
    }

    navigate("/mbs/patient-registration", {
      state: Object.keys(navigationState).length ? navigationState : null,
    });
  };

  return (
    <Stack spacing={2} alignItems="center">
      <Stack>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="body1">
            {t("mbsSearch.resultsHeading")}
          </Typography>
          <Typography variant="subtitle2">
            {t("mbsSearch.matchingRecords", { count: sortedPatients.length })}
          </Typography>
        </Stack>

        <DataGrid
          autoHeight
          rows={sortedPatients}
          getRowId={getPatientTableRowId}
          columns={columns}
          loading={loading}
          slots={{
            footer: PatientTableFooter,
            toolbar: GridToolbar,
            noRowsOverlay: EmptyStateOverlay,
            noResultsOverlay: EmptyStateOverlay,
          }}
          slotProps={{
            footer: {
              canSearchMore,
              handleSearchMore,
              loading,
              hasResults: sortedPatients.length > 0,
            },
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 300 },
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "skeleton",
            },
          }}
          disableMultipleRowSelection
          onRowClick={handleRowClick}
          rowSelectionModel={selectedRowId ? [selectedRowId] : []}
          onRowSelectionModelChange={(selectionModel) =>
            setSelectedRowId(selectionModel[0] || null)
          }
          pageSizeOptions={[50, 100, 200]}
          initialState={{
            columns: {
              columnVisibilityModel: {
                address: false,
                postalCode: false,
                city: false,
                province: false,
                primaryPhone: false,
                secondaryPhone: false,
                email: false,
                registrationNumber: false,
                healthCardProvince: false,
              },
            },
            pagination: { paginationModel: { pageSize: 50, page: 0 } },
          }}
          sx={{
            borderColor: "primary.main",
            borderRadius: BUTTON_BORDER_RADIUS,
          }}
        />
      </Stack>
      <Stack spacing={0.5} alignItems="center">
        <Button
          variant="contained"
          disabled={loading || disableCreatePatient}
          startIcon={<PersonAddAlt1Outlined />}
          onClick={handleCreateGuest}
        >
          {t("mbsSearch.createGuest")}
        </Button>
        {!loading && disableCreatePatient && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            {t("mbsSearch.createGuestDisabled")}
          </Typography>
        )}
      </Stack>
      <PatientDrawer
        open={openDrawer}
        toggleDrawer={(newOpen) => setOpenDrawer(newOpen)}
        patient={selectedPatient}
        setPatients={setPatients}
        onPatientUpdated={setSelectedPatient}
        matchesCurrentSearch={matchesCurrentSearch}
        bookAppointment={bookAppointment}
        handleUserSearch={handleSearch}
        isMBS
        inSystem={selectedPatient?.inSystem}
      />
    </Stack>
  );
};

export default PatientTable;
